import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { AmendPayload, NodeType } from '@/shared/types/tryIt';

import { ConversationReplay } from './ConversationReplay';
import type { EvaluationWithSimulation, SimulationSession } from '../../../types';
import {
  EVALUATION_STATUS_BADGE_FLAVOUR,
  EVALUATION_STATUS_TRANSLATION_KEYS,
} from '../../../types';

interface LeftPanelProps {
  evaluation: EvaluationWithSimulation;
  session: SimulationSession | null;
  sessionNodes: NodeType[];
  isInitialLoading: boolean;
  hasError: boolean;
  selectedTurnIndex: number;
  simulationContext: NonNullable<AmendPayload['simulation']>;
  onSelectTurn: (index: number) => void;
}

export const LeftPanel = ({
  evaluation,
  session,
  sessionNodes,
  isInitialLoading,
  hasError,
  selectedTurnIndex,
  simulationContext,
  onSelectTurn,
}: LeftPanelProps) => {
  const { t } = useTranslation();
  const { simulation } = evaluation;

  const statusFlavour = EVALUATION_STATUS_BADGE_FLAVOUR[evaluation.status];
  const statusLabel = t(EVALUATION_STATUS_TRANSLATION_KEYS[evaluation.status] ?? evaluation.status);

  // Every non-data branch (no session, loading, error, empty turns) ends up
  // with sessionNodes empty — single source of truth for the layout switch.
  const isEmpty = !sessionNodes.length;

  const renderConversation = () => {
    if (!evaluation.session_id) {
      return (
        <span className="text-sm text-foreground-muted">{t('simulations.no_conversation')}</span>
      );
    }

    if (isInitialLoading) {
      return <Spinner size="lg" />;
    }

    if (hasError) {
      return (
        <span className="text-sm text-destructive">
          {t('simulations.conversation_replay_error')}
        </span>
      );
    }

    if (!session || !sessionNodes.length) {
      return (
        <span className="text-sm text-foreground-muted">
          {t('simulations.conversation_replay')}
        </span>
      );
    }

    return (
      <ConversationReplay
        sessionNodes={sessionNodes}
        sessionId={evaluation.session_id}
        brainId={evaluation.brain_id}
        simulationContext={simulationContext}
        selectedIndex={selectedTurnIndex}
        onSelect={onSelectTurn}
      />
    );
  };

  return (
    <div className="flex h-full min-w-0 flex-1 basis-0 flex-col overflow-hidden rounded-lg border border-stroke bg-background">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-stroke px-4 py-3">
        <DialogTitle className="min-w-0 flex-1 truncate text-base leading-6 font-semibold text-foreground">
          {simulation.name}
        </DialogTitle>
        <Badge type="outline" flavour={statusFlavour} rounded={false}>
          {statusLabel}
        </Badge>
      </div>

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden',
          isEmpty && 'items-center justify-center'
        )}
      >
        {isEmpty ? (
          renderConversation()
        ) : (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto">
            {renderConversation()}
          </div>
        )}
      </div>
    </div>
  );
};
